import Swiper from 'swiper'
import { Navigation, Pagination, Autoplay } from 'swiper/modules'

// Usage:
// .swiper <id> <slidePerView (swiper-slide-auto/swiper-slide-md-2)> <autoplay (swiper-autoplay or swiper-autoplay-5000)> <direction (swiper-vertical)> <click-to-next (swiper-click-next)>
// .swiper#swiper-home-committee.swiper-slide-auto
// .swiper#swiper-discover.swiper-slide-1.swiper-autoplay
// .swiper#swiper-travel-info.swiper-vertical.swiper-click-next.swiper-autoplay-3000

const BREAKPOINT_MAP = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
}

const getBreakpointValue = (breakpoint) => BREAKPOINT_MAP[breakpoint] || null

const parseSwiperClasses = (container) => {
  let slidesPerView = 1 // Default value
  let autoplayDelay = null // Default: no autoplay
  let direction = 'horizontal' // Default direction
  let clickToNext = false // Default: no click to next
  let centered = false // Default: slides align to the start
  const breakpoints = {}

  Array.from(container.classList).forEach((className) => {
    // Handle auto slides
    if (className === 'swiper-slide-auto') {
      slidesPerView = 'auto'
      return
    }

    // Handle vertical direction
    if (className === 'swiper-vertical') {
      direction = 'vertical'
      return
    }

    // Handle click to next
    if (className === 'swiper-click-next') {
      clickToNext = true
      return
    }

    // Handle centered slides (coverflow-style carousels)
    if (className === 'swiper-centered') {
      centered = true
      return
    }

    // Handle autoplay
    if (className === 'swiper-autoplay') {
      autoplayDelay = 3000 // Default delay
      return
    }

    // Handle autoplay with custom delay (e.g., swiper-autoplay-5000)
    if (className.startsWith('swiper-autoplay-')) {
      const delay = parseInt(className.split('-')[2], 10)
      if (!isNaN(delay)) {
        autoplayDelay = delay
      }
      return
    }

    // Skip non-swiper classes
    if (!className.startsWith('swiper-slide-')) return

    const parts = className.split('-')

    if (parts.length === 3) {
      // Format: swiper-slide-N (e.g., swiper-slide-2)
      const num = parseInt(parts[2], 10)
      if (!isNaN(num)) {
        slidesPerView = num
      }
    } else if (parts.length === 4) {
      // Format: swiper-slide-BP-N (e.g., swiper-slide-md-4)
      const breakpoint = parts[2]
      const num = parseInt(parts[3], 10)

      if (!isNaN(num)) {
        const breakpointValue = getBreakpointValue(breakpoint)
        if (breakpointValue) {
          breakpoints[breakpointValue] = { slidesPerView: num }
        }
      }
    }
  })

  return {
    slidesPerView,
    breakpoints,
    autoplayDelay,
    direction,
    clickToNext,
    centered,
  }
}

const createBaseConfig = (swiperId) => ({
  modules: [Navigation, Pagination, Autoplay],
  spaceBetween: 20,
  loop: true,
  pagination: {
    el: `#${swiperId} .swiper-pagination`,
    clickable: true,
  },
  navigation: {
    nextEl: `#${swiperId} .swiper-button-next, [data-swiper-nav="${swiperId}"] .swiper-button-next-custom`,
    prevEl: `#${swiperId} .swiper-button-prev, [data-swiper-nav="${swiperId}"] .swiper-button-prev-custom`,
  },
})

const initSwipers = () => {
  const swipers = []

  // Find all elements with IDs starting with "swiper-"
  document.querySelectorAll('[id^="swiper-"]').forEach((container) => {
    const swiperId = container.id
    const {
      slidesPerView,
      breakpoints,
      autoplayDelay,
      direction,
      clickToNext,
      centered,
    } = parseSwiperClasses(container)

    // Create base configuration
    const config = createBaseConfig(swiperId)

    // Add direction if vertical
    if (direction === 'vertical') {
      config.direction = 'vertical'
      config.speed = 400
      config.spaceBetween = 0
    }

    // Add slidesPerView if it's not the default
    if (slidesPerView !== 1) {
      config.slidesPerView = slidesPerView
    }

    // Centre the active slide (coverflow-style carousels)
    if (centered) {
      config.centeredSlides = true
    }

    // Add breakpoints if any were found
    if (Object.keys(breakpoints).length > 0) {
      config.breakpoints = breakpoints
    }

    // Add autoplay if enabled
    if (autoplayDelay !== null) {
      config.autoplay = {
        delay: autoplayDelay,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      }
    }

    // Initialize Swiper
    const swiper = new Swiper(`#${swiperId}`, config)

    // Add click to next functionality if enabled
    if (clickToNext) {
      swiper.on('click', () => {
        swiper.slideNext()
      })
    }

    swipers.push({
      id: swiperId,
      swiper,
      element: container,
    })
  })

  return swipers
}

export default initSwipers
